import { QueryKeys } from "@Totum/Main/Constants";
import { ActiveTabKey, QueryKeys as GroupsQueryKeys } from "../constants";
import { MainContext } from "@Totum/Main/Context/provider";
import { IAddressDetailsPaged } from "@Totum/Main/Domain/IAddressDetails";
import { ICreditDetails, ICreditDetailsPaged } from "@Totum/Main/Domain/ICreditDetails";
import { ILicenseCreationDetails } from "@Totum/Main/Domain/ILicenseDetails";
import { IGroupAssignmentDetails, IUserGroupDetails } from "@Totum/Main/Domain/IUserGroupDetails";
import AddressService from "../../../services/AddressService";
import CreditService from "../../../services/CreditService";
import LicenseService from "../../../services/LicenseService";
import UserGroupService from "../../../services/UserGroupService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getSystemId, refreshUserSignature } from "@Totum/Main/Utils";
import { notification } from "antd";
import { useContext } from "react";
import UserService from "../../../services/UserService";
import { IRoleAssignmentDetails, IRoleAssignmentPaged } from "@Totum/Main/Domain/IRoleDetails";

/**
 * Mutation for creating a group credit
 * @example
 * const { mutate: createGroupCredit } = useCreateGroupCredit();
 *
 * const handleCreate = (credit: ICreditDetailsPaged) => {
 *   createGroupCredit(credit)
 * };
 */
export const useCreateGroupCredit = () => {
    const queryClient = useQueryClient();
    const { setCurrentGroup } = useContext(MainContext);

    return useMutation(
        async (credit: ICreditDetailsPaged) => {
            const service = new CreditService();
            const newCredit = await service.createGroup(credit.ownerId, credit, credit.instanceId);
            if (!newCredit.hasErrors) {
                setCurrentGroup({ instance: credit.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Group credit account created." });
                await queryClient.invalidateQueries([QueryKeys.UserGroupCredits]);
                await queryClient.invalidateQueries([QueryKeys.UserGroupAddresses]);
            },
        }
    );
};

/**
 * Mutation for creating a group
 * @example
 * const { mutate: createGroup } = useCreateGroup();
 *
 * const handleCreate = (group: IUserGroupDetails) => {
 *   createGroup(group)
 * };
 */
export const useCreateGroup = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async (group: IUserGroupDetails) => {
            const service = new UserGroupService();
            return await service.create(group);
        },
        {
            onSuccess: async (res) => {
                if (res?.value?.id) {
                    // Refresh the user signature to ensure the new group is added to the user's signature.
                    await refreshUserSignature();

                    notification.success({ message: "New group created." });
                    await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
                    await queryClient.invalidateQueries([GroupsQueryKeys.UserGroupCurrent]);
                    await queryClient.invalidateQueries([GroupsQueryKeys.GroupsList]);
                } else {
                    notification.error({ message: "Error creating new group." });
                }
            },
        }
    );
};

/**
 * Mutation for creating a license
 * @example
 * const { mutate: createGroupLicense } = useCreateGroupLicense();
 *
 * const handleCreate = (license: ILicenseCreationDetails) => {
 *   createGroupLicense(license)
 * };
 */
export const useCreateGroupLicense = () => {
    const queryClient = useQueryClient();
    const { setCurrentGroup } = useContext(MainContext);

    return useMutation(
        async (licenseDetails: ILicenseCreationDetails) => {
            notification.info({ message: "Creating license..." });
            const service = new LicenseService();
            const newLicense = await service.create(licenseDetails);
            if (!newLicense.hasErrors) {
                setCurrentGroup({ instance: licenseDetails.instanceId });
                await queryClient.resetQueries([GroupsQueryKeys.GroupsList]);
                await queryClient.invalidateQueries([GroupsQueryKeys.UserGroupCurrent]);
                await queryClient.resetQueries([QueryKeys.UserGroupAddresses]);
                await queryClient.resetQueries([QueryKeys.UserGroupCredits]);
                await queryClient.resetQueries([QueryKeys.UserGroupLicenses]);
                await queryClient.resetQueries([QueryKeys.GroupUserRoles]);
                await queryClient.resetQueries([QueryKeys.GroupUserAddresses]);
                await queryClient.resetQueries([QueryKeys.GroupUserCredits]);
                await queryClient.resetQueries([QueryKeys.GroupUserLicenses]);
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Group license created." });
            },
        }
    );
};

/**
 * Mutation for creating a license
 * @example
 * const { mutate: createGroupAddress } = useCreateGroupAddress();
 *
 * const handleCreate = (address: IAddressDetailsPaged) => {
 *   createGroupAddress(address)
 * };
 */
export const useCreateGroupAddress = () => {
    const queryClient = useQueryClient();
    const { setCurrentGroup } = useContext(MainContext);

    return useMutation(
        async (address: IAddressDetailsPaged) => {
            const service = new AddressService();
            const newAddress = await service.createGroup(address.ownerId, address, address.instanceId);
            if (!newAddress.hasErrors) {
                setCurrentGroup({ instance: address.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Group address created." });
                await queryClient.invalidateQueries([QueryKeys.UserGroupAddresses]);
            },
        }
    );
};

/**
 * Create user address.
 */
export const useCreateUserAddress = (groupId?: string) => {
    const queryClient = useQueryClient();
    const { setCurrentUser } = useContext(MainContext);

    return useMutation(
        async (addressDetails: IAddressDetailsPaged) => {
            const service = new AddressService();
            const newAddress = await service.createUser(addressDetails, addressDetails.instanceId);
            if (!newAddress.hasErrors) {
                setCurrentUser({ instance: addressDetails.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Address created." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserAddresses, groupId]);
            },
        }
    );
};

/**
 * Create user license.
 */
export const useCreateUserLicense = (groupId?: string) => {
    const queryClient = useQueryClient();
    const { setCurrentUser } = useContext(MainContext);

    return useMutation(
        async (licenseDetails: ILicenseCreationDetails) => {
            notification.info({ message: "Creating license." });
            const service = new LicenseService();
            const newLicense = await service.create(licenseDetails);
            if (!newLicense.hasErrors) {
                setCurrentUser({ instance: licenseDetails.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "User license created." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserLicenses, groupId]);
            },
        }
    );
};

/**
 * Create user credit
 */
export const useCreateUserCredit = (groupId?: string) => {
    const queryClient = useQueryClient();
    const { setCurrentUser } = useContext(MainContext);

    return useMutation(
        async (creditDetails: ICreditDetailsPaged) => {
            const service = new CreditService();
            const newCredit = await service.createUser(creditDetails, creditDetails.instanceId);
            if (!newCredit.hasErrors) {
                setCurrentUser({ instance: creditDetails.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "User credit account created." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserCredits, groupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserAddresses, groupId]);
            },
        }
    );
};

/**
 * Create role assignment.
 */
export const useAddUserRole = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async (role: IRoleAssignmentPaged) => {
            const service = new UserService();
            await service.assignRoles([role.userId], role.roleId, getSystemId());
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Role assigned." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, getSystemId()]);
                await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
                await queryClient.invalidateQueries([GroupsQueryKeys.UserRoles]);
            },
        }
    );
};

/**
 * Add member to subgroup. Called via the assignment menu.
 */
export const useAddUserToGroup = () => {
    const queryClient = useQueryClient();

    return useMutation(async (assignment: IGroupAssignmentDetails) => {
        const service = new UserGroupService();
        const res = await service.addMembers(assignment.groupId, assignment.userIds);
        if (!res.hasErrors) {
            notification.success({ message: "Added to group." });
            await queryClient.invalidateQueries([QueryKeys.UserGroups]);
            await queryClient.invalidateQueries([QueryKeys.GroupMembers, assignment.groupId]);
            await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, assignment.groupId]);
            await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
            await queryClient.invalidateQueries([GroupsQueryKeys.GroupUsers]);
            await queryClient.invalidateQueries([GroupsQueryKeys.GroupsList]);
        } else {
            notification.warning({ message: "Already added." });
        }
    });
};

/**
 * Remove role assignment.
 */

export const useRemoveRoleAssignment = (groupId?: string, activeTabKey?: string) => {
    const queryClient = useQueryClient();
    const activeTabGroupId = activeTabKey === ActiveTabKey.Groups ? groupId : getSystemId();

    return useMutation(
        async (role: IRoleAssignmentDetails) => {
            const service = new UserService();
            await service.removeRoles([role.userId], role.roleId, activeTabGroupId);
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Role removed." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, activeTabGroupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupMembers, activeTabGroupId]);
                await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
                await queryClient.invalidateQueries([GroupsQueryKeys.UserRoles]);
                await queryClient.invalidateQueries([GroupsQueryKeys.Users]);
            },
        }
    );
};

/**
 * Remove group members.
 */

export const useRemoveGroupMembers = (groupId?: string) => {
    const queryClient = useQueryClient();
    const { setCurrentUser } = useContext(MainContext);

    return useMutation(
        async (userIds: string[]) => {
            const service = new UserGroupService();
            return await service.removeMembers(userIds, groupId);
        },
        {
            onSuccess: async (res) => {
                if (!res?.errors?.length) {
                    await queryClient.invalidateQueries([QueryKeys.UserGroups]);
                    await queryClient.invalidateQueries([GroupsQueryKeys.GroupUsers]);
                    await queryClient.invalidateQueries([QueryKeys.GroupMembers, groupId]);
                    await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
                    notification.success({ message: "Removed from group." });
                    setCurrentUser(null);
                } else {
                    res.errors.forEach((message: string) => {
                        notification.error({ message });
                    });
                }
            },
        }
    );
};

/**
 * Delete user credit mutation
 */
export const useCreditDeleteUser = (groupId: string) => {
    const queryClient = useQueryClient();
    const { setCurrentUser } = useContext(MainContext);

    return useMutation(
        async (creditDetails: ICreditDetails) => {
            const service = new CreditService();
            const deleteCredit = await service.delete([creditDetails.creditId]);
            if (!deleteCredit.hasErrors) {
                setCurrentUser({ instance: creditDetails.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "User credit account deleted." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserCredits, groupId]);
            },
        }
    );
};

/**
 * Delete group credit.
 */
export const useCreditDeleteGroup = () => {
    const queryClient = useQueryClient();
    const { setCurrentGroup } = useContext(MainContext);

    return useMutation(
        async (credit: ICreditDetails) => {
            const service = new CreditService();
            const deleteCredit = await service.delete([credit.creditId]);
            if (!deleteCredit.hasErrors) {
                setCurrentGroup({ instance: credit.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Group credit account deleted." });
                await queryClient.invalidateQueries([QueryKeys.UserGroupCredits]);
            },
        }
    );
};

/**
 * Delete group.
 */
export const useDeleteGroup = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async (groupId?: string) => {
            notification.info({ message: "Deleting group." });
            const service = new UserGroupService();
            const res = await service.delete(groupId);
            if (!res.hasErrors) {
                await queryClient.resetQueries([GroupsQueryKeys.GroupsList]);
                await queryClient.resetQueries([GroupsQueryKeys.UserGroupCurrent]);
                await queryClient.resetQueries([QueryKeys.UserGroupAddresses]);
                await queryClient.resetQueries([QueryKeys.UserGroupCredits]);
                await queryClient.resetQueries([QueryKeys.UserGroupLicenses]);
                await queryClient.resetQueries([QueryKeys.GroupUserRoles]);
                await queryClient.resetQueries([QueryKeys.GroupUserAddresses]);
                await queryClient.resetQueries([QueryKeys.GroupUserCredits]);
                await queryClient.resetQueries([QueryKeys.GroupUserLicenses]);
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Group and all child groups deleted." });
                await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
            },
        }
    );
};
